import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Users, UserCog, Calendar, Building2, LogOut, Menu, X, ClipboardCheck, Grid3X3, LayoutGrid, LayoutDashboard, Repeat, ShieldCheck, ArrowRightLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const navItems = [
  { name: 'Panel Central', href: '/ese/dashboard', icon: LayoutDashboard },
  { name: 'Flujo de Onboarding', href: '/ese/panel', icon: Grid3X3 },
  { name: 'Trabajadores', href: '/ese/trabajadores', icon: Users },
  { name: 'Usuarios', href: '/ese/usuarios', icon: UserCog },
  { name: 'Capacitaciones', href: '/ese/sesiones', icon: Calendar },
  { name: 'Resultados', href: '/ese/resultados', icon: ClipboardCheck },
  { name: 'Matriz Capacitaciones', href: '/ese/matriz', icon: LayoutGrid },
  { name: 'Planes HS', href: '/ese/planes-hs', icon: ShieldCheck },
  { name: 'Reasignaciones', href: '/ese/reasignaciones', icon: ArrowRightLeft },
];

export const EseLayout = ({ children }) => {
  const { user, logout, token, hasMultiplePerfiles, switchPerfil } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoImage, setLogoImage] = useState(() => localStorage.getItem('cached_logo_url'));
  const [empresa, setEmpresa] = useState(null);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await axios.get(`${API}/configuracion`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.logo_image) {
          let imgUrl = res.data.logo_image;
          if (!imgUrl.startsWith('http') && !imgUrl.startsWith('data:'))
            imgUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
          setLogoImage(imgUrl);
          localStorage.setItem('cached_logo_url', imgUrl);
        }
      } catch {}
    };
    if (!logoImage) fetchLogo();
  }, [token, logoImage]);

  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        const res = await axios.get(`${API}/ese/mi-empresa`, { headers: { Authorization: `Bearer ${token}` } });
        setEmpresa(res.data);
      } catch {}
    };
    fetchEmpresa();
  }, [token]);

  const toTitleCase = (s) => s ? s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase()) : '';

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-[#1a365d] to-[#153e75] z-40 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 border-b border-white/10 flex items-center justify-center px-4">
          {logoImage ? (
            <div className="w-[200px] h-[48px] overflow-hidden flex items-center justify-center">
              <img src={logoImage} alt="Logo" className="max-w-full max-h-full object-contain brightness-0 invert"
                onError={() => { setLogoImage(null); localStorage.removeItem('cached_logo_url'); }}
                data-testid="sidebar-logo" />
            </div>
          ) : (
            <span className="font-bold text-lg text-white tracking-tight">Lomas Bayas</span>
          )}
        </div>

        {empresa && (
          <div className="px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-300 shrink-0" />
              <p className="text-xs text-blue-200 font-medium truncate">{toTitleCase(empresa.razon_social)}</p>
            </div>
          </div>
        )}

        <nav className="px-3 py-4 space-y-1">
          {navItems.map(item => {
            const active = location.pathname === item.href;
            return (
              <Link key={item.href} to={item.href} onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active ? 'bg-blue-500/20 text-blue-200 font-medium' : 'text-blue-100/70 hover:bg-white/10 hover:text-white'
                }`} data-testid={`nav-${item.name.toLowerCase()}`}>
                <item.icon className="w-4.5 h-4.5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 px-3 py-4 border-t border-white/10">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-blue-200">{user?.nombre} {user?.apellido}</p>
            <p className="text-[11px] text-blue-300/50 truncate">{user?.email}</p>
          </div>
          {hasMultiplePerfiles && hasMultiplePerfiles() && (
            <button onClick={() => { if (switchPerfil()) navigate('/seleccionar-perfil'); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-blue-200/70 hover:bg-amber-500/10 hover:text-amber-200 w-full transition-colors mb-1"
              data-testid="cambiar-perfil-btn">
              <Repeat className="w-4.5 h-4.5" />
              Cambiar de perfil
            </button>
          )}
          <button onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-blue-200/60 hover:bg-red-500/10 hover:text-red-300 w-full transition-colors"
            data-testid="logout-btn">
            <LogOut className="w-4.5 h-4.5" />
            Cerrar Sesion
          </button>
        </div>
      </aside>

      <div className="lg:pl-64 min-h-screen flex flex-col">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 h-14">
          <button className="lg:hidden p-2 hover:bg-slate-100 rounded-lg" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div />
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-blue-700 bg-blue-50 px-3 py-1 rounded-full">Gestión de Trabajadores</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};
